import { Currency, Filter, Lang, Search } from 'types';
import { formatJsonToQueryString, formatQueryStringToJson } from '../utils';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { computed, ComputedRef } from 'vue';

export function useHotelSearchRouteHandler() {
  const route = useRoute();
  const router = useRouter();
  const store = useStore();

  const filters: ComputedRef<Filter.HotelCollectionFilterModel | undefined> =
    computed(() => store.state.search.filters);

  const locale: ComputedRef<Lang.LangItem> = computed(
    () => store.state.support.lang,
  );

  const currency: ComputedRef<Currency.CurrencyItem> = computed(
    () => store.state.support.currency,
  );

  const setCurrency = (value: Currency.CurrencyItem) => {
    store.commit('support/setCurrency', value);
  };
  const setLang = (value: Lang.LangItem) => {
    store.commit('support/setLang', value);
  };

  const handleRoute = async (
    incomingRoute,
  ): Promise<Search.SearchRouteParams | null> => {
    try {
      const id = incomingRoute.params.id;

      if (!id) {
        return null;
      }

      const { query } = incomingRoute;

      const args: Search.SearchRouteParams = {
        id,
      };

      if (query.in) {
        args.in = query.in as string;
      }

      if (query.out) {
        args.out = query.out as string;
      }

      if (query.guests) {
        args.guests = query.guests as string;
      }

      if (query.d) {
        args.destintaion = query.rooms as string;
      }

      if (query.repeat) {
        args.repeat = query.repeat as string;
      }

      if (query.hotelId) {
        args.hotelId = query.hotelId as string;
      }

      if (query.hotel) {
        args.hotel = query.hotel as string;
      }

      if (query.type) {
        args.type = query.type as string;
      }

      if (query.city) {
        args.city = query.city as string;
      }

      if (query.state) {
        args.state = query.state as string;
      }

      if (query.country) {
        args.country = query.country as string;
      }

      if (query.filters) {
        const filters = parseFilters(incomingRoute);

        args.filters = filters;
      }

      return args;
    } catch (error) {
      return null;
    }
  };

  const composeFilters = (
    changed: Filter.Query,
  ): Promise<Filter.HotelCollectionFilterModel> => {
    return store.dispatch('search/syncFilters', {
      syncWith: filters.value || Filter.HotelCollectionFilterModel.compose(),
      filters: changed,
    });
  };

  const emitFilters = async (changed: Filter.Query) => {
    const formatted = await zipFilters(changed);

    const query = { ...route.query, filters: formatted };

    router.push({ query });
  };

  const parseFilters = (incomingRoute): Filter.Query => {
    try {
      const current = incomingRoute.query.filters as string;

      const parsed = formatQueryStringToJson<Filter.MinifiedQuery>(current);

      return unminify(parsed);
    } catch (error) {
      return null;
    }
  };

  const handleFiltersChange = async (
    incomingRoute,
  ): Promise<Filter.RouteUpdateCallback> => {
    try {
      const filters = parseFilters(incomingRoute);

      if (!filters) {
        return null;
      }

      const currencyChanged = isCurrencyChanged(filters);
      const localeChanged = isLocaleChanged(filters);

      const currency = Currency.currenciesList().find(
        it => it.currency === filters.currency,
      );

      if (currencyChanged) {
        setCurrency(currency);
      }

      const lang = Lang.langsList().find(it => it.locale === filters.locale);

      if (localeChanged) {
        setLang(lang);
      }

      const filtersChanged = await store.dispatch(
        'search/applyFilter',
        filters,
      );

      const { forceClear, forceMap } = filters;

      const res =
        filtersChanged ||
        currencyChanged ||
        localeChanged ||
        forceClear ||
        forceMap;

      return {
        needUpdate: res,
        filtersChanged,
        currencyChanged,
        localeChanged,
        forceMap,
        forceClear,
      };
    } catch (error) {
      return null;
    }
  };

  const minify = async (
    changed: Filter.Query,
  ): Promise<Filter.MinifiedQuery> => {
    const synced = await composeFilters(changed);

    const filters = synced.minify();

    const singleEvents = [];

    if (changed.forceClear === true) {
      singleEvents.push(Filter.SingleForceEvent.Clear);
    }

    if (changed.forceMap === true) {
      singleEvents.push(Filter.SingleForceEvent.Map);
    }

    if (singleEvents.length > 0) {
      filters.fe = singleEvents.join(',');
    }

    if (changed.bounds) {
      const bounds = changed.bounds;

      const sw = bounds.sw.join(',');
      const ne = bounds.ne.join(',');
      const coordsString = `${sw};${ne}`;

      filters.b = coordsString;
    }

    if (currency.value) {
      filters.crr = currency.value.currency;
    }

    if (locale.value) {
      filters.intl = locale.value.locale;
    }

    return filters;
  };

  const zipFilters = async (changed: Filter.Query): Promise<string> => {
    const filters = await minify(changed);

    return formatJsonToQueryString(filters);
  };

  const isCurrencyChanged = (changed: Filter.Query): boolean => {
    try {
      return changed.currency !== currency.value.currency;
    } catch (error) {
      return true;
    }
  };

  const isLocaleChanged = (changed: Filter.Query): boolean => {
    try {
      return changed.locale !== locale.value.locale;
    } catch (error) {
      return true;
    }
  };

  const unminify = (payload: Filter.MinifiedQuery): Filter.Query => {
    try {
      const query = Filter.HotelCollectionFilterModel.unminify(payload);

      if (payload.b) {
        const bounds = payload.b.split(';');
        const sw = bounds[0].split(',').map(parseFloat);
        const ne = bounds[1].split(',').map(parseFloat);

        query.bounds = {
          sw,
          ne,
        };
      }

      return query;
    } catch (error) {
      return {};
    }
  };

  return {
    emitFilters,
    handleFiltersChange,
    handleRoute,
    zipFilters,
    minify,
  };
}
